/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output
} from '@angular/core';
import * as moment from 'moment/moment';

import {DATE_FORMAT} from '../../../../../app.constants';

interface Event {
  type: string;
  id: number;
  attributes: {
    value: any;
    hoursPaid: number;
    hoursWorked: number;
    begindate: string;
    enddate: string;
    type: string;
    empContract: number;
  };
  view: {
    color: string;
    transId: string;
    readOnly: boolean;
  };
}

@Component({
  selector: 'gp-calendar-presence',
  template: `
    <a
      class="fc-day-grid-event fc-h-event fc-event fc-start fc-end fc-draggable fc-resizable"
      *ngIf="eventOpts?.view"
      [style.backgroundColor]="eventOpts.view.color"
      [style.borderColor]="eventOpts.view.color"
      [popover]="dayPop"
      popoverTitle="{{ eventOpts.view.transId | translate}}"
      triggers="mouseenter:mouseleave"
      container="body">
      <ng-container *ngIf="!(isReadOnly || eventOpts.view.readOnly)">
        <span class="fc-icon fc-icon-left-single-arrow"
              style="left: 0;"
              *ngIf="this.isHover && isBegining"
              (click)="decreaseStart()">
        </span>
        <span class="fc-icon fc-icon-right-single-arrow"
              style="left: 0; margin-left: 15px;"
              *ngIf="!this.isSingleDay && this.isHover && isBegining"
              (click)="increaseStart()">
        </span>
        <div class="icon-btn" data-action="edit" style="left: calc(50% - 20px);" *ngIf="isHover">
          <i class="fa fa-light fa-pencil-alt pointer"></i>
        </div>
        <div class="icon-btn" data-action="delete" style="left: 50%;" *ngIf="isHover">
          <i class="fa fa-light fa-trash-alt pointer"></i>
        </div>
      </ng-container>
      <div class="fc-content">
        <span class="fc-title">{{ eventOpts.view.transId | translate }}</span>
      </div>
      <ng-container *ngIf="!(isReadOnly || eventOpts.view.readOnly)">
          <span class="fc-icon fc-icon-left-single-arrow"
                style="right: 0; margin-right: 15px;"
                *ngIf="!this.isSingleDay && this.isHover && isEnding"
                (click)="decreaseEnd()">
        </span>
        <span class="fc-icon fc-icon-right-single-arrow"
              style="right: 0;"
              *ngIf="this.isHover && isEnding"
              (click)="increaseEnd()">
        </span>
      </ng-container>
    </a>
    <ng-template #dayPop>
      <ul class="list-unstyled" style="min-width: 130px">
        <li class="nowrap" *ngIf="eventOpts.attributes.hoursWorked">
          {{ 'PAYROLL_PREPARE.hours_worked' | translate }}: {{ eventOpts.attributes.hoursWorked }}
        </li>
        <li class="nowrap" *ngIf="eventOpts.attributes.hoursPaid">
          {{ 'PAYROLL_PREPARE.hours_paid' | translate }}: {{ eventOpts.attributes.hoursPaid }}
        </li>
        <li class="nowrap">
          {{ 'PAYROLL_PREPARE.start_date' | translate }}: {{ eventOpts.attributes.begindate | gpDate }}
        </li>
        <li class="nowrap">
          {{ 'PAYROLL_PREPARE.end_date' | translate }}: {{ eventOpts.attributes.enddate | gpDate }}
        </li>
        <li class="nowrap">
          {{ 'PAYROLL_PREPARE.value' | translate }}: {{ eventOpts.attributes.value }}
        </li>
      </ul>
    </ng-template>
  `,
  styleUrls: ['./calendar-presence.component.css'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CalendarPresenceComponent implements OnChanges {

  @Input()
  public isReadOnly: boolean = true;

  public height: string;
  public isHover: boolean = false;
  public isBegining: boolean;
  public isEnding: boolean;
  public isSingleDay: boolean;

  @Input('event')
  public eventOpts: Event;

  @Input('weekview')
  public weekview: boolean = false;

  @Input('color')
  public color: string = '#fff';

  @Input('date')
  public date: string;

  @Output()
  public eventChanged: EventEmitter<Event> = new EventEmitter<Event>();

  @HostListener('mouseenter')
  public mouseenter() {
    if (!this.isReadOnly) {
      this.isHover = true;
    }
  }

  @HostListener('mouseleave')
  public mouseleave() {
    this.isHover = false;
  }

  constructor(public element: ElementRef) {

  }

  public ngOnChanges() {
    if (this.eventOpts) {
      this.isBegining = moment(this.date).isSame(this.eventOpts.attributes.begindate);
      this.isEnding = moment(this.date).isSame(this.eventOpts.attributes.enddate);
      this.isSingleDay =
        this.eventOpts.attributes.begindate === this.eventOpts.attributes.enddate;
      // this.element.nativeElement.style.marginTop = this.weekview ? '2px' : '20px';
      this.element.nativeElement.style.marginTop = '1px';
      this.element.nativeElement.style.marginBottom = '1px';
    }
  }

  public increaseEnd() {
    this.eventOpts.attributes.enddate = moment(this.eventOpts.attributes.enddate)
      .add(1, 'days').format(DATE_FORMAT);
    this.eventChanged.emit(this.eventOpts);
  }

  public decreaseEnd() {
    this.eventOpts.attributes.enddate = moment(this.eventOpts.attributes.enddate)
      .add(-1, 'days').format(DATE_FORMAT);
    this.eventChanged.emit(this.eventOpts);
  }

  public increaseStart() {
    this.eventOpts.attributes.begindate = moment(this.eventOpts.attributes.begindate)
      .add(1, 'days').format(DATE_FORMAT);
    this.eventChanged.emit(this.eventOpts);
  }

  public decreaseStart() {
    this.eventOpts.attributes.begindate = moment(this.eventOpts.attributes.begindate)
      .add(-1, 'days').format(DATE_FORMAT);
    this.eventChanged.emit(this.eventOpts);
  }
}
